import { useState } from 'react';
import { lovemoBrand, lovemoNavItems } from '@/mocks/lovemoContent';
import { lovemoScrollToSection } from '@/hooks/useLovemoScrollTo';
import LovemoButton from '@/components/base/LovemoButton';
import LovemoThemeToggle from '@/components/feature/LovemoThemeToggle';
import type { LovemoTheme } from '@/hooks/useLovemoTheme';

interface LovemoHeaderProps {
  theme: LovemoTheme;
  onToggleTheme: () => void;
  onDownload: () => void;
}

export default function LovemoHeader({ theme, onToggleTheme, onDownload }: LovemoHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const handleNav = (sectionId: string) => {
    setMenuOpen(false);
    lovemoScrollToSection(sectionId);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50">
      <div className="mx-auto flex h-[72px] w-full max-w-[1280px] items-center justify-between gap-4 px-4 md:px-8">
        <a
          href={lovemoBrand.siteUrl}
          className="group flex cursor-pointer items-center gap-2.5"
          aria-label="Lovemo官方网站"
        >
          <span className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl ring-1 ring-primary-200/70 transition-transform duration-300 group-hover:scale-105">
            <img
              src={lovemoBrand.logo}
              alt="Lovemo官方应用图标"
              className="h-10 w-10 object-cover"
              width={40}
              height={40}
            />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-bold tracking-tight text-foreground-950">
              Lovemo
            </span>
            <span className="mt-0.5 font-label text-[11px] tracking-wide text-primary-600">
              {lovemoBrand.domain}
            </span>
          </span>
        </a>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {lovemoNavItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleNav(item.id)}
              className="cursor-pointer rounded-full px-3.5 py-2 font-label text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2.5 md:gap-3">
          <LovemoThemeToggle theme={theme} onToggle={onToggleTheme} />
          <LovemoButton size="sm" onClick={onDownload} aria-label="立即下载Lovemo">
            <i className="ri-download-2-line text-base" aria-hidden="true" />
            <span className="hidden sm:inline">立即下载</span>
          </LovemoButton>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开导航菜单"
            aria-expanded={menuOpen}
            className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-200/80 text-foreground-800 transition-colors hover:bg-background-100 lg:hidden"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-2-line text-xl'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="animate-lovemo-pop border-t border-background-200/70 bg-background-50 px-4 py-4 lg:hidden">
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {lovemoNavItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNav(item.id)}
                className="cursor-pointer rounded-lg bg-background-100 px-4 py-3 text-left font-label text-sm font-medium text-foreground-800 transition-colors hover:bg-primary-50 hover:text-primary-600"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}